import { formatNumberPositionSize, formatNumberUSD, formatPercentage } from "util/format"

import { Liquidity } from "@perp/sdk-curie"
import Big from "big.js"
import { useMemo } from "react"
import { useLiquidityDetail } from "sdk-react/liquidity/useLiquidityDetail"

const PLACEHOLDER = "-,--"

export function useLiquidityDetailStrings(liquidity: Liquidity) {
    const { lowerTickPrice, upperTickPrice, market } = liquidity
    const { baseSymbol, quoteSymbol, tickerSymbol } = market
    const {
        rangeType,
        pendingFee: fee,
        amountBase,
        amountQuote,
        amountBaseAsQuote,
        amountTotalAsQuote,
    } = useLiquidityDetail(liquidity)
    const { baseRatio, quoteRatio } = useMemo(() => {
        if (!amountQuote || !amountBaseAsQuote || !amountTotalAsQuote) {
            return { baseRatio: undefined, quoteRatio: undefined }
        }
        if (amountTotalAsQuote.lte(0)) {
            return { baseRatio: undefined, quoteRatio: undefined }
        }
        const baseRatio = amountBaseAsQuote.div(amountTotalAsQuote)
        const quoteRatio = new Big(1).sub(baseRatio)
        return { baseRatio, quoteRatio }
    }, [amountBaseAsQuote, amountQuote, amountTotalAsQuote])

    const feeStr = fee ? formatNumberUSD({ num: fee, displayLength: 1 }) : PLACEHOLDER
    const amountBaseStr = amountBase ? formatNumberPositionSize(amountBase) : PLACEHOLDER
    const amountQuoteStr = amountQuote ? formatNumberUSD({ num: amountQuote }) : PLACEHOLDER
    const amountTotalAsQuoteStr = amountTotalAsQuote
        ? formatNumberUSD({ num: amountTotalAsQuote, displayLength: 1 })
        : PLACEHOLDER
    const basePercentageStr = baseRatio ? formatPercentage(baseRatio) : PLACEHOLDER
    const quotePercentageStr = quoteRatio ? formatPercentage(quoteRatio) : PLACEHOLDER
    const lowerTickPriceStr = lowerTickPrice.lte(0.000001) ? "0" : formatNumberUSD({ num: lowerTickPrice })
    const upperTickPriceStr = upperTickPrice.gte(1000000) ? "∞" : formatNumberUSD({ num: upperTickPrice })

    return {
        baseSymbol,
        quoteSymbol,
        tickerSymbol,
        rangeType,
        baseRatio,
        feeStr,
        amountBaseStr,
        amountQuoteStr,
        amountTotalAsQuoteStr,
        basePercentageStr,
        quotePercentageStr,
        lowerTickPriceStr,
        upperTickPriceStr,
    }
}
